import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TOption, TStatusBadgeProps } from '@treezor/design-system';

import { useAppSelector } from '@/hooks/redux-helpers';

import { defaultLanguage } from '@/i18n/index';

import { TKycDocumentStatus } from '@/features/kyc/store/models/kyc.model';

export function useKycDocuments() {
  const { i18n, t } = useTranslation();

  const language = i18n.language || defaultLanguage;

  const documentTypes = i18n.getResource(language, 'translation', `pages.currentProfiles.kyc.documents.types`);
  const displayOnlyDocumentTypes = i18n.getResource(
    language,
    'translation',
    `pages.currentProfiles.kyc.documents.displayOnlyTypes`,
  );

  const allDocuments = useMemo(
    () => ({ ...documentTypes, ...displayOnlyDocumentTypes }),
    [documentTypes, displayOnlyDocumentTypes],
  );

  const documentTypeOptions = useMemo<TOption[]>(
    () =>
      Object.keys(documentTypes)
        .map((typeId) => ({
          label: documentTypes[typeId],
          value: typeId,
        }))
        .sort((documentA, documentB) => (documentA.label <= documentB.label ? -1 : 1)),
    [documentTypes],
  );

  const clientName = useAppSelector((state) => {
    const name = state?.dashboardConfig?.config?.site?.name;
    if (name?.length <= 15) return name;
    return state?.dashboardConfig?.config?.site?.shortName ?? 'You';
  });

  const documentStatusProps: Record<TKycDocumentStatus, TStatusBadgeProps> = {
    TO_REVIEW: {
      color: 'orange',
      label: t('pages.currentProfiles.kyc.documents.kycStatus.toReview', { name: clientName }),
    },
    REVIEWED: {
      color: 'green',
      label: t('pages.currentProfiles.kyc.documents.kycStatus.reviewed', { name: clientName }),
    },
    REVIEWED_TO_SUBMIT: {
      color: 'mediumOrange',
      label: t('pages.currentProfiles.kyc.documents.kycStatus.reviewedSubmit'),
    },
    PENDING_VALIDATION: {
      color: 'darkOrange',
      label: t('pages.currentProfiles.kyc.documents.kycStatus.pendingValidation'),
    },
    VALIDATED: {
      color: 'green',
      label: t('pages.currentProfiles.kyc.documents.kycStatus.validated'),
    },
    REFUSED_CLIENT: {
      color: 'red',
      label: t('pages.currentProfiles.kyc.documents.kycStatus.refused', { name: clientName }),
    },
    REFUSED: {
      color: 'red',
      label: t('pages.currentProfiles.kyc.documents.kycStatus.refusedTreezor'),
    },
    CANCELED: {
      color: 'gray',
      label: t('pages.currentProfiles.kyc.documents.kycStatus.canceled'),
    },
  };

  const documentTranslation = useCallback(
    (typeId: number) => allDocuments[typeId] || t('pages.currentProfiles.kyc.documents.notDefined'),
    [allDocuments, t],
  );

  return {
    documentTypeOptions,
    allDocuments,
    documentTranslation,
    documentStatusProps,
  };
}
